import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { AbstractControl, Validators, FormControl } from '@angular/forms';
import { FieldType } from '@ngx-formly/core';
import { FormViewService, IntegrationMappingService, SubmissionService, VehicleListService} from '@xilo-mono/form-contracts';
import { SubmissionSessionService } from '../../services/submission-session.service';
import { DatepickerI18nComponent } from '@xilo-mono/shared/datepicker-i18n';
import { delay, map, Subscription, startWith, ReplaySubject, takeUntil } from 'rxjs';
import { InitialAddress } from '../address-v2/address-v2.component';
import moment from 'moment';
import { changeInputCSS } from '../helper/form';
import { findBestMatch, removeMoneyFormatting } from '@xilo-mono/form-helper';
import { get } from 'lodash';

@Component({
  selector: 'xilo-mono-form-viewer-input',
  templateUrl: './input.component.html',
  styleUrls: ['../../form-viewer.component.scss', './input.component.scss'],
})
export class CustomInputComponent
  extends FieldType
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('dateInput', { static: false }) dateInput: ElementRef<HTMLInputElement>;
  isInvalid = false;
  errorMessage = false;
  changeInputCSS = changeInputCSS;
  vehicleSubscription: Subscription;
  datePickerHeader = DatepickerI18nComponent;
  // AddressV2 vars
  newAddressV2 = '';
  initialAddress: InitialAddress;
  maxDate: Date = null;
  private readonly destroyed$ = new ReplaySubject<boolean>(1);
  private isRegisteredWithService = false;
  constructor(
    private formViewService: FormViewService,
    private submissionService: SubmissionService,
    private vehicleListService: VehicleListService,
    private submissionSessionService: SubmissionSessionService,
    private integrationMappingService: IntegrationMappingService
  ) {
    super();
  }

  get isAddressV2() {
    return this.field.templateOptions?.version === 2;
  }

  ngOnInit() {
    if (this.to.type === 'email') {
      const validators = [Validators.email, this.topLevelValidator];
      if (this.to.required) {
        validators.push(Validators.required);
      }
      this.formControl.setValidators(validators);
      this.formControl.updateValueAndValidity();
    } else if (this.to.type === 'date') {
      const validators = [];
      if (this.to.required) validators.push(Validators.required);
      if (this.to.maxDate === 'today') {
        this.maxDate = new Date();
        validators.push(this.disableFutureDatesValidator)
      }
      if (validators.length > 0) {
        this.formControl.setValidators(validators);
        this.formControl.updateValueAndValidity();
      }
      // Set default date is no value exists
      if (!this.formControl.getRawValue()) {
        switch(this.to.defaultDate) {
          case 'today':
            this.dateChange({ value: new Date() });
            break;
          case 'tomorrow':
            this.dateChange({ value: new Date(new Date().setDate(new Date().getDate() + 1)) });
            break;
          case '7_days':
            this.dateChange({ value: new Date(new Date().setDate(new Date().getDate() + 7))} );
            break;
          case '14_days':
            this.dateChange({ value: new Date(new Date().setDate(new Date().getDate() + 14))} );
            break;
          case '1_month':
            this.dateChange({ value: new Date(new Date().setMonth(new Date().getMonth() + 1))} );
            break;
          case '2_months':
            this.dateChange({ value: new Date(new Date().setMonth(new Date().getMonth() + 2))} );
            break;
          case '3_months':
            this.dateChange({ value: new Date(new Date().setMonth(new Date().getMonth() + 3))} );
            break;
          case '6_months':
            this.dateChange({ value: new Date(new Date().setMonth(new Date().getMonth() + 6))} );
            break;
          case '1_year':
            this.dateChange({ value: new Date(new Date().setFullYear(new Date().getFullYear() + 1)) });
            break;
        }
      }
    }
  }

  ngAfterViewInit(): void {
    // Ensure formControl exists
    if (!this.formControl) {
      console.error('CustomInputComponent: formControl is missing in ngAfterViewInit.');
      return;
    }

    // --- Register with service if applicable ---
    if (this.to.type === 'email') {
      this.submissionSessionService.registerField(this.formControl as FormControl, this.field, 'email');
      this.isRegisteredWithService = true;
    }
    // --- End Registration ---

    this.formViewService.addressV2.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((initialAddress) => {
      if (initialAddress) {
        this.initialAddress = initialAddress;
      }
    });
    this.setupField();
  }

  // Custom validator to disable future dates
  disableFutureDatesValidator(control: AbstractControl) {
    const selectedDate = new Date(control.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to the start of today

    // Check if selected date is after today
    if (selectedDate > today) {
      return { futureDate: true }; // Return error if date is in the future
    }
    return null; // Valid if the date is today or in the past
  }

  topLevelValidator(
    control: AbstractControl
  ): { [key: string]: boolean } | null {
    const value = control.value;
    if (value && typeof value === 'string' && !/\.[a-z]{2,}$/i.test(value)) {
      return { noTLD: true };
    }
    return null;
  }

  setupField() {
    if (this.to.dataKey === 'vin') {
      this.vehicleSubscription = this.formControl.valueChanges
        .pipe(
          startWith(this.formControl.value),
          delay(500),
          map((val) => {
            if (val && val.length === 17) {
              this.getVehicleDetailsByVIN(val);
            }
          }),
          takeUntil(this.destroyed$)
        )
        .subscribe();
    }
  }

  // dateChange: formats UTC timestamp to YYYY-MM-DD for consistent formats in integrations and forms
  dateChange(e) {
    const date = moment(e.value, 'YYYY-MM-DD').format('YYYY-MM-DD');
    this.formControl.patchValue(date);
    if (date != '') {
      this.updateMetadata(e);
    }
  }

  isInputType(type: string) {
    if (type === 'date' && this.to.type === 'date') {
      return true;
    } else if (type === 'suffix' && this.to.suffix) {
      return true;
    } else if (type === 'prefix' && this.to.prefix) {
      return true;
    } else if (
      type === 'input' &&
      this.to.type !== 'date' &&
      !this.to.suffix &&
      !this.to.prefix
    ) {
      return true;
    }
  }

  returnMask() {
    if (this.to.type === 'number') {
      return '0*';
    } else {
      return 'A*';
    }
  }

  updateMetadata(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target?.value) {
      let { value } = target;
      if (typeof value === 'string' && this.to.type !== 'date') {
        try {
          value = target.value.trim();
        } catch (error) {
          value = target.value;
        }
        if (this.to.prefix && value.startsWith(this.to.prefix)) value = value.slice(this.to.prefix.length);
        if (this.to.suffix && value.endsWith(this.to.suffix)) value = value.slice(0, value.length - this.to.suffix.length);
        if (this.to.type === 'number') {
          this.formControl.setValue(removeMoneyFormatting(value));
        } else {
          this.formControl.setValue(value);
        }
      }
      if (Array.isArray(this.to.clientTag)) {
        this.to.clientTag.forEach((tag) => {
          this.submissionService.onUpdateMetadata({ key: tag, value }, this.field);
        });
      } else if (this.to.clientTag) {
        this.submissionService.onUpdateMetadata({ key: this.to.clientTag, value }, this.field);
      }
      if (this.to.type !== 'date') {
        if (this.to?.isAEPull) {
          this.integrationMappingService.initDataPull(
            this.to?.pullDataTrigger,
            this.to?.dataPullMappingId,
            this.field?.parent?.parent?.templateOptions?.subIndex,
            true,
            { type: 'DATA', loadSeconds: 30 }
          );
        } else {
          this.integrationMappingService.initDataPull(
            this.to?.pullDataTrigger,
            this.to?.dataPullMappingId,
            this.field?.parent?.parent?.templateOptions?.subIndex
          );
        }
      }
    }
    if (this.to.className !== 'xilo-input') {
      this.formViewService.updateForm.next(true);
    }
    this.changeAddressMeta();
  }

  // Add handleBlur method
  handleBlur(event: FocusEvent) {
    if (!this.formControl) {
      console.error('CustomInputComponent: formControl is missing in handleBlur.');
      return;
    }
    // Only call service if the field type was registered
    if (this.to.type === 'email') {
      this.submissionSessionService.handleBlur(this.formControl as FormControl, this.field, 'email');
    } else {
      this.updateMetadata(event);
    }
  }

  toType() {
    if (
      this.to.type === 'text' ||
      this.to.type === 'checkbox' ||
      this.to.type === 'number' ||
      this.to.type === 'date' ||
      this.to.type === 'email' ||
      this.to.type === 'password'
    ) {
      return this.to.type;
    } else {
      return 'text';
    }
  }

  getVehicleDetailsByVIN(vin: string) {
    // Get vehicle info including year, make and model from api
    this.vehicleListService.getEZVehicleByVin(vin).pipe(
      takeUntil(this.destroyed$)
    ).subscribe((result) => {
      // check for API response error
      if (get(result, '[0].Status') && result[0].Status !== 'VINNotFound') {
        const vehicleYearField = this.field.parent.fieldGroup.find((field) => {
          return field.templateOptions.dataKey === 'vehicleYear';
        });
        if (vehicleYearField) {
          const vyf = vehicleYearField.formControl;
          vyf.patchValue(result[0].Year);
        }
        const vehicleMakeField = this.field.parent.fieldGroup.find((field) => {
          return field.templateOptions.dataKey === 'vehicleMake';
        });
        if (vehicleMakeField) {
          const vmf = vehicleMakeField.formControl;
          vmf.patchValue(result[0].Make);
        }
        const vehicleModelField = this.field.parent.fieldGroup.find((field) => {
          return field.templateOptions.dataKey === 'vehicleModel';
        });
        if (vehicleModelField) {
          const vmof = vehicleModelField.formControl;
          vmof.patchValue(result[0].Model?.toString());
        }
        const vehicleBodyStyleField = this.field.parent.fieldGroup.find(
          (field) => {
            return field.templateOptions.dataKey === 'vehicleBodyStyle';
          }
        );
        if (vehicleBodyStyleField) {
          setTimeout(() => {
            const vbsF = vehicleBodyStyleField.formControl;
            const vbOptions =
              (vehicleBodyStyleField.templateOptions.options as Array<{
                label: string;
                value: string;
              }>) || [];
            if (vbOptions.length > 0) {
              const closestMatchResult = findBestMatch(
                result[0].CombinedBody,
                vbOptions.map((opt) => opt.value)
              );
              if (
                closestMatchResult &&
                closestMatchResult.bestMatch &&
                closestMatchResult.bestMatch.target
              ) {
                vbsF.patchValue(closestMatchResult.bestMatch.target);
              } else {
                console.log('No model options found.');
              }
            }
          }, 1500);
        }
      }
    });
  }

  // changeAddressMeta: change the address string with users input changes and call the /submission/update endpoint
  changeAddressMeta() {
    const addressFieldKey = this.field.templateOptions.lockedTo; // unique key identifying the address group
    const address = this.field.parent.fieldGroup.find((field) => {
      // read the current address template
      return field.templateOptions.dataKey === addressFieldKey;
    });
    if (this.isAddressV2 && address.formControl.value != null) {
      const key = this.field.templateOptions.dataKey; // key collects the input values unique key
      const value = this.formControl.value; // new value added yo UI
      if (key.includes('city')) {
        // look for initial value and replace. update addressV2 value
        this.newAddressV2 = address.formControl.value.replace(
          this.initialAddress.city,
          value
        );
        this.formViewService.addressV2.next({
          ...this.initialAddress,
          city: value,
        });
        // update address with newly replaced string
        address.formControl.patchValue(this.newAddressV2);
        // ensure active input has latest changes
        this.formControl.patchValue(value);
      } else if (key.includes('zipCode')) {
        this.newAddressV2 = address.formControl.value.replace(
          this.initialAddress.zipCode,
          value
        );
        this.formViewService.addressV2.next({
          ...this.initialAddress,
          zipCode: value,
        });
        address.formControl.patchValue(this.newAddressV2);
        this.formControl.patchValue(value);
      } else if (key.includes('streetAddress2')) {
        this.newAddressV2 = address.formControl.value.replace(
          this.initialAddress.streetAddress2,
          value
        );
        this.submissionService.onUpdateMetadata(
          {
            key: 'unitNumber',
            value,
          },
          this.field
        );
        this.formViewService.addressV2.next({
          ...this.initialAddress,
          streetAddress2: value,
        });
        address.formControl.patchValue(this.newAddressV2);
        this.formControl.patchValue(value);
      }
    }
  }

  onDatepickerClosed(): void {
    setTimeout(() => {
      if (this.dateInput?.nativeElement) {
        this.dateInput.nativeElement.focus();
        const len = this.dateInput.nativeElement.value.length;
        this.dateInput.nativeElement.setSelectionRange(len, len);
      }
    }, 100);
  }

  ngOnDestroy() {
    // Unregister from service if applicable
    if (this.isRegisteredWithService) {
      this.submissionSessionService.unregisterField(this.field, 'email');
    }
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

   // Keep getError method for template validation display
  getError() {
    if (this.formControl?.hasError('required')) {
      return 'This field is required.';
    }
    if (this.formControl?.hasError('email')) {
      return 'Invalid email format.';
    }
    if (this.formControl?.hasError('noTLD')) {
      return 'Email must include a valid domain (e.g., .com).';
    }
    if (this.formControl?.hasError('futureDate')) {
      return 'Date cannot be in the future.';
    }

    return this.formControl?.invalid && this.formControl?.touched ? 'Invalid input' : '';
   }
}
